import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  ListSubheader,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  FileDownloadOutlined,
  FileUploadOutlined,
  CheckCircleOutline,
  CancelOutlined,
  AddCircleOutline,
  AutoFixHigh,
  Close,
  DeleteOutline,
  HistoryOutlined,
  RefreshOutlined,
  StopCircleOutlined,
  ExpandMore,
  ExpandLess,
  FilterList,
  FilterListOff,
  OpenInNew,
  ViewColumn,
  SearchOff,
  InfoOutlined,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchCustomers,
  fetchCustomerWithBrands,
  fetchProducts,
  fetchReferenceData,
  createCustomer,
  importFile,
  setFilter,
  resetFilters,
  clearImportResult,
  clearError,
  fetchRunHistory,
  saveRunHistory,
  deleteRunHistory,
  submitGenerationJob,
  fetchGenerationJobs,
  pollGenerationJob,
  cancelGenerationJob,
} from '../store/slices/customerProfilingSlice';
import type { CPProduct, CPRunHistoryItem, CPGenerationJob } from '../store/slices/customerProfilingSlice';
import apiClient from '../utils/axiosConfig';

// Crawl depth presets. Descriptions mirror DEPTH_CONFIG in the CP pipeline
// (pages per seed / link depth / retry rounds) so the tooltips stay truthful.
// max_rounds there counts ALL rounds and round 1 is the normal pass, so the
// retries a user can actually see are max_rounds - 1 — and only when a round
// comes in under the yield threshold, hence "up to".
const DEPTH_OPTIONS = [
  { value: 1, label: 'Shallow', description: 'Quick scan — ~10 pages per source, no retries. Fastest, finds the fewest products.' },
  { value: 2, label: 'Medium', description: 'Balanced — ~25 pages per source, up to 1 retry round. The default for most companies.' },
  { value: 3, label: 'Deep', description: 'Wide crawl — ~50 pages per source, deeper link following, up to 2 retry rounds. Noticeably slower.' },
  { value: 0, label: 'Exhaustive', description: 'Maximum coverage — ~100 pages per source, deepest link following, up to 2 retry rounds. Slowest; large portfolios can return thousands of products.' },
];

const DEPTH_LABELS: Record<number, string> = DEPTH_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {} as Record<number, string>);

// Hidden on request (issue #27). Flip to true to bring the control back.
const SHOW_IMPORT_BUTTON: boolean = false;
const SHOW_FEATURES_FILTER: boolean = false;

function formatDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

// ── History Sidebar (SummaryManager pattern) ─────────────────────────────────

interface HistorySidebarProps {
  items: CPRunHistoryItem[];
  loading: boolean;
  selectedRunId: string | null;
  onRestore: (item: CPRunHistoryItem) => void;
  onDelete: (id: string) => void;
  onDownloadCsv: (item: CPRunHistoryItem) => void;
  onRefresh: () => void;
  isCpAdmin: boolean;
  isMobile: boolean;
  isTablet: boolean;
}

const HistorySidebar: React.FC<HistorySidebarProps> = ({
  items,
  loading,
  selectedRunId,
  onRestore,
  onDelete,
  onDownloadCsv,
  onRefresh,
  isCpAdmin,
  isMobile,
  isTablet,
}) => {
  return (
    <Box
      sx={{
        width: isMobile ? '100%' : (isTablet ? 280 : 300),
        minWidth: isMobile ? 'auto' : (isTablet ? 270 : 290),
        height: isMobile ? 'auto' : '100%',
        maxHeight: isMobile ? '50vh' : '100%',
        backgroundColor: 'background.paper',
        color: 'text.primary',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1050,
        borderRight: isMobile ? 'none' : (theme) => `1px solid ${theme.palette.divider}`,
        borderBottom: isMobile ? (theme) => `1px solid ${theme.palette.divider}` : 'none',
      }}
    >
      <Box
        sx={{
          p: isMobile ? 1.5 : 2,
          pb: 1,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isMobile ? 'center' : 'space-between',
            position: 'relative',
            mb: 0,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              textAlign: isMobile ? 'center' : 'left',
              flex: isMobile ? 1 : 'unset',
              fontSize: isMobile ? '0.7rem' : '0.9rem',
            }}
          >
            Run History ({items.length})
          </Typography>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={onRefresh} sx={{ p: 0.5 }}>
              <RefreshOutlined sx={{ fontSize: isMobile ? 22 : 24 }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Divider sx={{ mt: 1 }} />
      </Box>
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          pt: 1,
          px: isMobile ? 1.5 : 2,
          pb: isMobile ? 1.5 : 2,
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? 1.5 : 2,
          scrollbarGutter: 'stable',
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: '4px' },
          '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.3)' },
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: isMobile ? 'center' : 'flex-start' }}>
            <CircularProgress size={16} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Loading runs...
            </Typography>
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <HistoryOutlined sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No saved runs yet
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              Generate a portfolio to see it here
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {items.map((item) => (
              <Card
                key={item.id}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: (theme) => theme.shadows[3],
                    bgcolor: 'action.hover',
                  },
                  border: '2px solid',
                  borderColor: selectedRunId === item.id ? 'primary.main' : 'transparent',
                  bgcolor: selectedRunId === item.id ? 'rgba(163,24,48,0.04)' : 'transparent',
                }}
                onClick={() => onRestore(item)}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, textAlign: isMobile ? 'center' : 'left', position: 'relative' }}>
                  <Box sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    zIndex: 1,
                  }}>
                    <Tooltip title="Download CSV">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDownloadCsv(item); }} sx={{ p: 0.25 }}>
                        <FileDownloadOutlined sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    {isCpAdmin && (
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(item.id); }} sx={{ p: 0.25 }}>
                          <DeleteOutline sx={{ fontSize: 16, color: 'error.main' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  <Box sx={{ pr: 5 }}>
                    <Box sx={{ mb: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                        {item.customer_name || 'Unknown'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
                      <Chip
                        label={`${item.product_count} products`}
                        size="small"
                        sx={{ fontSize: '0.7rem', height: 22, fontWeight: 600 }}
                        color="primary"
                        variant="outlined"
                      />
                      {(() => {
                        const f = item.filters_json || {};
                        const tags: string[] = [];
                        if (f.depth != null) tags.push(DEPTH_LABELS[f.depth] || `Depth ${f.depth}`);
                        if (f.regions?.length) tags.push(...f.regions);
                        if (f.countries?.length) tags.push(...f.countries);
                        if (f.industry) tags.push(f.industry);
                        if (f.marketing_categories?.length) tags.push(...f.marketing_categories);
                        if (f.product_category) tags.push(f.product_category);
                        if (f.format?.length) tags.push(...f.format);
                        if (f.subcategories?.length) tags.push(...f.subcategories);
                        if (f.positioning?.length) tags.push(...(Array.isArray(f.positioning) ? f.positioning : [f.positioning]));
                        if (f.product_application?.length) tags.push(...(Array.isArray(f.product_application) ? f.product_application : [f.product_application]));
                        if (f.serving_temperature?.length) tags.push(...(Array.isArray(f.serving_temperature) ? f.serving_temperature : [f.serving_temperature]));
                        if (f.plant_based_categories?.length) tags.push(...f.plant_based_categories);
                        if (f.claims_filter?.length) tags.push(`${f.claims_filter.length} claims`);
                        if (f.features_filter?.length) tags.push(`${f.features_filter.length} features`);
                        return tags.slice(0, 3).map((tag, i) => (
                          <Chip
                            key={i}
                            label={tag}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 20, '& .MuiChip-label': { px: 1 }, color: 'text.secondary', borderColor: 'divider' }}
                          />
                        ));
                      })()}
                      {(() => {
                        const f = item.filters_json || {};
                        const total = [
                          f.depth != null ? 1 : 0,
                          ...(f.regions || []),
                          ...(f.countries || []),
                          f.industry ? 1 : 0,
                          ...(f.marketing_categories || []),
                          f.product_category ? 1 : 0,
                          ...(f.format || []),
                          ...(f.subcategories || []),
                          ...(Array.isArray(f.positioning) ? f.positioning : f.positioning ? [f.positioning] : []),
                          ...(Array.isArray(f.product_application) ? f.product_application : f.product_application ? [f.product_application] : []),
                          ...(Array.isArray(f.serving_temperature) ? f.serving_temperature : f.serving_temperature ? [f.serving_temperature] : []),
                          ...(f.plant_based_categories || []),
                          ...(f.claims_filter?.length ? ['c'] : []),
                          ...(f.features_filter?.length ? ['f'] : []),
                        ].length;
                        return total > 3 ? (
                          <Chip
                            label={`+${total - 3}`}
                            size="small"
                            sx={{ fontSize: '0.65rem', height: 20, '& .MuiChip-label': { px: 1 }, bgcolor: 'action.hover' }}
                          />
                        ) : null;
                      })()}
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
                      {formatDate(item.created_at)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ── Claims Chip Renderer ─────────────────────────────────────────────────────

const ClaimsChips: React.FC<{ claims: string }> = ({ claims }) => {
  if (!claims) return null;
  const items = claims.split(', ').filter(Boolean);
  if (items.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
      {items.map((c, i) => (
        <Chip
          key={i}
          label={c}
          size="small"
          variant="outlined"
          sx={{
            fontSize: '0.65rem',
            height: 20,
            maxWidth: 120,
            '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
          }}
        />
      ))}
    </Box>
  );
};

// ── Truncated Cell Helper ────────────────────────────────────────────────────

const TruncCell = ({ text, maxWidth = 160, sx }: { text: string | null; maxWidth?: number; sx?: Record<string, any> }) => (
  <Tooltip title={text || ''} placement="top" arrow disableHoverListener={!text}>
    <Box sx={{ maxWidth, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...sx }}>
      {text || ''}
    </Box>
  </Tooltip>
);

// ── Active Filter Summary ────────────────────────────────────────────────────

const FilterSummary: React.FC<{
  filters: Record<string, any>;
  customers: { id: string; name: string }[];
}> = ({ filters, customers }) => {
  const parts: string[] = [];
  if (filters.customer_id) {
    const name = customers.find(c => c.id === filters.customer_id)?.name;
    if (name) parts.push(`Customer: ${name}`);
  }
  if (filters.region) parts.push(`Region: ${filters.region}`);
  if (filters.country) parts.push(`Country: ${filters.country}`);
  if (filters.industry) parts.push(`Industry: ${filters.industry}`);
  if (filters.marketing_category) parts.push(`Product Category: ${filters.marketing_category}`);
  if (filters.product_category) parts.push(`AGRANA Product Type: ${filters.product_category}`);
  if (filters.subcategory) parts.push(`Subcategory: ${filters.subcategory}`);
  if (filters.product_application) parts.push(`Product Application: ${filters.product_application}`);
  if (filters.serving_temperature) parts.push(`Serving Temperature: ${filters.serving_temperature}`);
  if (filters.plant_based_category) parts.push(`Plant-based Category: ${filters.plant_based_category}`);
  if (filters.positioning) parts.push(`Positioning: ${filters.positioning}`);
  if (filters.search) parts.push(`Search: "${filters.search}"`);
  if (filters.agrana_present && filters.agrana_present !== 'all') parts.push(`AGRANA Present: ${filters.agrana_present === 'true' ? 'Yes' : 'No'}`);
  if (filters.claims?.length) parts.push(`${filters.claims.length} claim${filters.claims.length > 1 ? 's' : ''}`);

  if (parts.length === 0) return null;

  return (
    <Typography
      variant="caption"
      sx={{
        color: 'text.secondary',
        fontSize: '0.7rem',
        ml: 1,
        display: 'inline',
        fontStyle: 'italic',
      }}
    >
      Filtered by: {parts.join(' \u00b7 ')}
    </Typography>
  );
};

// ── Component ───────────────────────────────────────────────────────────────

const CustomerProfiling: React.FC = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const {
    customers,
    selectedCustomer,
    products,
    totalProducts,
    referenceData,
    filters,
    loading,
    customersLoading,
    importResult,
    importing,
    error,
    runHistory,
    runHistoryLoading,
    generationJobs,
    generationJobsLoading,
    submittingJob,
  } = useAppSelector((state) => state.customerProfiling);

  const user = useAppSelector((state) => state.auth.user);
  const isCpAdmin = !!(
    user &&
    (['admin', 'cp-admin', 'superadmin'].includes(user.role) ||
      user.service_permissions?.some(
        (sp) => sp.service === 'customer_profiling' && sp.role === 'admin'
      ))
  );

  const location = useLocation();
  const activeTab = location.pathname === '/cp/generate' ? 1 : 0;

  // Local state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [newCustomerDialogOpen, setNewCustomerDialogOpen] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [importSourceType, setImportSourceType] = useState('manual');
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [showAllColumns, setShowAllColumns] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Generate form state
  const [genCompany, setGenCompany] = useState('');
  const [genDepth, setGenDepth] = useState(2);
  const [genRegions, setGenRegions] = useState<string[]>([]);
  const [genCountries, setGenCountries] = useState<string[]>([]);
  // Track uncommitted text in all freeSolo+multiple Autocomplete fields.
  // MUI only commits typed text on Enter — blur/click-away silently discards it.
  // We merge any pending text into the array at submit time.
  const [genInputs, setGenInputs] = useState<Record<string, string>>({});
  const [genIndustry, setGenIndustry] = useState('');
  const [genMarketingCategories, setGenMarketingCategories] = useState<string[]>([]);
  const [genProductCategory, setGenProductCategory] = useState('');
  const [genFormat, setGenFormat] = useState<string[]>([]);
  const [genPositioning, setGenPositioning] = useState<string[]>([]);
  const [genProductApplication, setGenProductApplication] = useState<string[]>([]);
  const [genClaimsFilter, setGenClaimsFilter] = useState<string[]>([]);
  const [genFeaturesFilter, setGenFeaturesFilter] = useState<string[]>([]);
  const [genServingTemperature, setGenServingTemperature] = useState<string[]>([]);
  const [genPlantBasedCategories, setGenPlantBasedCategories] = useState<string[]>([]);

  // ── Initialize data ─────────────────────────────────────────────────────

  useEffect(() => {
    dispatch(fetchCustomers());
    dispatch(fetchReferenceData());
    dispatch(fetchRunHistory());
    dispatch(fetchGenerationJobs());
  }, [dispatch]);

  const handleSubmitGenerationJob = useCallback(async () => {
    if (!genCompany.trim()) return;
    // Merge any text typed in freeSolo fields that wasn't confirmed with Enter
    const merge = (arr: string[], field: string) => {
      const v = (genInputs[field] || '').trim();
      return v && !arr.includes(v) ? [...arr, v] : arr;
    };
    const jobParams = {
      company: genCompany.trim(),
      depth: genDepth,
      regions: merge(genRegions, 'regions'),
      countries: merge(genCountries, 'countries'),
      industry: genIndustry || null,
      marketing_categories: merge(genMarketingCategories, 'marketing_categories'),
      product_category: genProductCategory || null,
      format: merge(genFormat, 'format'),
      positioning: merge(genPositioning, 'positioning'),
      product_application: merge(genProductApplication, 'product_application'),
      claims_filter: merge(genClaimsFilter, 'claims_filter'),
      // While the field is hidden the form must not silently ship a value —
      // pre-filling from a past run can still populate genFeaturesFilter.
      features_filter: SHOW_FEATURES_FILTER ? merge(genFeaturesFilter, 'features_filter') : [],
      serving_temperature: merge(genServingTemperature, 'serving_temperature'),
      plant_based_categories: merge(genPlantBasedCategories, 'plant_based_categories'),
    };
    const result = await dispatch(submitGenerationJob(jobParams));
    if (submitGenerationJob.fulfilled.match(result)) {
      await dispatch(saveRunHistory({
        customer_id: undefined,
        customer_name: genCompany.trim(),
        label: null,
        filters_json: { ...jobParams, generation_job_id: result.payload.id },
        product_count: 0,
      }));
      dispatch(fetchRunHistory());
      setSnackbar({ open: true, message: 'Generation job submitted successfully', severity: 'success' });
      setGenCompany('');
      setGenDepth(2);
      setGenRegions([]);
      setGenCountries([]);
      setGenInputs({});
      setGenIndustry('');
      setGenMarketingCategories([]);
      setGenProductCategory('');
      setGenFormat([]);
      setGenPositioning([]);
      setGenProductApplication([]);
      setGenClaimsFilter([]);
      setGenFeaturesFilter([]);
      setGenServingTemperature([]);
      setGenPlantBasedCategories([]);
    }
  }, [dispatch, genCompany, genDepth, genRegions, genCountries, genInputs, genIndustry, genMarketingCategories, genProductCategory, genFormat, genPositioning, genProductApplication, genClaimsFilter, genFeaturesFilter, genServingTemperature, genPlantBasedCategories]);

  const handlePollJob = useCallback((jobId: string) => {
    dispatch(pollGenerationJob(jobId)).then((result) => {
      if (pollGenerationJob.fulfilled.match(result)) {
        const job = result.payload as CPGenerationJob;
        if (job.status === 'completed') {
          setSnackbar({ open: true, message: `Job completed: ${job.products_found} products saved`, severity: 'success' });
          dispatch(fetchCustomers());
          dispatch(fetchRunHistory());
        } else if (job.status === 'failed') {
          setSnackbar({ open: true, message: `Job failed: ${job.error_message}`, severity: 'error' });
        } else {
          setSnackbar({ open: true, message: `Status: ${job.progress || job.status}`, severity: 'info' });
        }
      } else {
        setSnackbar({ open: true, message: 'Failed to poll job status', severity: 'error' });
      }
    }).catch(() => {
      setSnackbar({ open: true, message: 'Failed to poll job status', severity: 'error' });
    });
  }, [dispatch]);

  const handleCancelJob = useCallback((jobId: string) => {
    if (!window.confirm('Cancel this generation job? Pages already scraped are discarded.')) return;
    dispatch(cancelGenerationJob(jobId)).then((result) => {
      if (cancelGenerationJob.fulfilled.match(result)) {
        setSnackbar({ open: true, message: 'Job cancelled', severity: 'info' });
      } else {
        const detail = (result.payload as string) || 'Failed to cancel job';
        setSnackbar({ open: true, message: detail, severity: 'error' });
      }
    }).catch(() => {
      setSnackbar({ open: true, message: 'Failed to cancel job', severity: 'error' });
    });
  }, [dispatch]);

  // Auto-poll active generation jobs every 10 seconds.
  // 'pending' must be included — that's the CP VM's queue state, distinct
  // from 'accepted' (initial AI-Hub state) and 'running' (CP VM scraping).
  // Without it, jobs sitting in the CP VM's queue never refresh in the UI.
  useEffect(() => {
    const activeJobs = generationJobs.filter(j =>
      j.status === 'accepted' || j.status === 'pending' || j.status === 'running'
    );
    if (activeJobs.length === 0) return;
    const interval = setInterval(() => {
      activeJobs.forEach(job => {
        dispatch(pollGenerationJob(job.id)).then((result) => {
          if (pollGenerationJob.fulfilled.match(result)) {
            const updated = result.payload as CPGenerationJob;
            if (updated.status === 'completed') {
              setSnackbar({ open: true, message: `Job completed: ${updated.products_found} products saved`, severity: 'success' });
              dispatch(fetchCustomers());
              dispatch(fetchRunHistory());
            } else if (updated.status === 'failed') {
              setSnackbar({ open: true, message: `Job failed: ${updated.error_message}`, severity: 'error' });
            }
          }
        });
      });
    }, 10000);
    return () => clearInterval(interval);
  }, [dispatch, generationJobs]);

  // When customer changes, fetch their brands
  useEffect(() => {
    if (filters.customer_id) {
      dispatch(fetchCustomerWithBrands(filters.customer_id));
    }
  }, [dispatch, filters.customer_id]);

  // Fetch products when filters change
  useEffect(() => {
    dispatch(fetchProducts());
  }, [dispatch, filters]);

  // ── Derived options (from reference data) ───────────────────────────────

  const marketingMenuItems = useMemo(() => {
    if (!referenceData) return [<MenuItem key="all" value="">All Product Categories</MenuItem>];

    const selectedIndustry = filters.industry;

    if (selectedIndustry && referenceData.marketing_categories[selectedIndustry]) {
      const scoped = referenceData.marketing_categories[selectedIndustry];
      return [
        <MenuItem key="all" value="">All Product Categories</MenuItem>,
        ...scoped.map(v => (
          <MenuItem key={v} value={v}>{v}</MenuItem>
        )),
      ];
    }

    return [
      <MenuItem key="all" value="">All Product Categories</MenuItem>,
      ...Object.entries(referenceData.marketing_categories).flatMap(([ind, cats]) => [
        <ListSubheader key={`${ind}-hdr`}>{ind}</ListSubheader>,
        ...cats.map(v => (
          <MenuItem key={`${ind}:${v}`} value={v}>{v}</MenuItem>
        )),
      ]),
    ];
  }, [referenceData, filters.industry]);

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleExportCsv = useCallback(async () => {
    try {
      const params: Record<string, string | string[]> = {};
      if (filters.customer_id) params.customer_id = filters.customer_id;
      if (filters.generation_job_id) params.generation_job_id = filters.generation_job_id;
      if (filters.region) params.region = filters.region;
      if (filters.country) params.country = filters.country;
      if (filters.industry) params.industry = filters.industry;
      if (filters.marketing_category) params.marketing_category = filters.marketing_category;
      if (filters.product_category) params.product_category = filters.product_category;
      if (filters.product_application) params.product_application = filters.product_application;
      if (filters.serving_temperature) params.serving_temperature = filters.serving_temperature;
      if (filters.plant_based_category) params.plant_based_category = filters.plant_based_category;
      if (filters.category) params.category = filters.category;
      if (filters.subcategory) params.subcategory = filters.subcategory;
      if (filters.positioning) params.positioning = filters.positioning;
      if (filters.agrana_present !== 'all') params.agrana_present = filters.agrana_present;
      if (filters.search) params.search = filters.search;

      // Build claims as repeat-key params (claims=val1&claims=val2) for FastAPI
      const parts: string[] = [];
      const qs = new URLSearchParams(params as Record<string, string>).toString();
      if (qs) parts.push(qs);
      if (filters.claims.length > 0) parts.push(filters.claims.map(c => `claims=${encodeURIComponent(c)}`).join('&'));
      const exportUrl = `/api/cp/export/csv${parts.length ? '?' + parts.join('&') : ''}`;

      const response = await apiClient.get(exportUrl, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const customerName = selectedCustomer?.name || 'portfolio';
      a.download = `${customerName.toLowerCase().replace(/\s+/g, '_')}_portfolio.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setSnackbar({ open: true, message: 'CSV export failed', severity: 'error' });
    }
  }, [filters, selectedCustomer]);

  const handleExportExcel = useCallback(async () => {
    try {
      const params: Record<string, string | string[]> = {};
      if (filters.customer_id) params.customer_id = filters.customer_id;
      if (filters.generation_job_id) params.generation_job_id = filters.generation_job_id;
      if (filters.region) params.region = filters.region;
      if (filters.country) params.country = filters.country;
      if (filters.industry) params.industry = filters.industry;
      if (filters.marketing_category) params.marketing_category = filters.marketing_category;
      if (filters.product_category) params.product_category = filters.product_category;
      if (filters.product_application) params.product_application = filters.product_application;
      if (filters.serving_temperature) params.serving_temperature = filters.serving_temperature;
      if (filters.plant_based_category) params.plant_based_category = filters.plant_based_category;
      if (filters.category) params.category = filters.category;
      if (filters.subcategory) params.subcategory = filters.subcategory;
      if (filters.positioning) params.positioning = filters.positioning;
      if (filters.agrana_present !== 'all') params.agrana_present = filters.agrana_present;
      if (filters.search) params.search = filters.search;

      // Build claims as repeat-key params (claims=val1&claims=val2) for FastAPI
      const parts: string[] = [];
      const qs = new URLSearchParams(params as Record<string, string>).toString();
      if (qs) parts.push(qs);
      if (filters.claims.length > 0) parts.push(filters.claims.map(c => `claims=${encodeURIComponent(c)}`).join('&'));
      const exportUrl = `/api/cp/export/excel${parts.length ? '?' + parts.join('&') : ''}`;

      const response = await apiClient.get(exportUrl, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const customerName = selectedCustomer?.name || 'portfolio';
      a.download = `${customerName.toLowerCase().replace(/\s+/g, '_')}_portfolio.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setSnackbar({ open: true, message: 'Excel export failed', severity: 'error' });
    }
  }, [filters, selectedCustomer]);

  const handleDownloadTemplate = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/cp/import/template', { responseType: 'blob' });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'customer_profiling_template.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setSnackbar({ open: true, message: 'Template download failed', severity: 'error' });
    }
  }, []);

  const handleFileImport = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const result = await dispatch(importFile({
      file,
      sourceType: importSourceType,
      customerId: filters.customer_id || undefined,
    }));

    if (importFile.fulfilled.match(result)) {
      const res = result.payload;
      setSnackbar({
        open: true,
        message: `Import complete: ${res.rows_imported} imported, ${res.rows_updated} updated, ${res.rows_failed} failed`,
        severity: res.rows_failed > 0 ? 'info' : 'success',
      });
      dispatch(fetchProducts());
      dispatch(fetchCustomers());
    } else {
      setSnackbar({ open: true, message: 'Import failed', severity: 'error' });
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
    setImportDialogOpen(false);
  }, [dispatch, importSourceType, filters.customer_id]);

  const handleCreateCustomer = useCallback(async () => {
    if (!newCustomerName.trim()) return;
    const result = await dispatch(createCustomer({ name: newCustomerName.trim() }));
    if (createCustomer.fulfilled.match(result)) {
      dispatch(setFilter({ customer_id: result.payload.id }));
      setNewCustomerDialogOpen(false);
      setNewCustomerName('');
      setSnackbar({ open: true, message: `Customer "${result.payload.name}" created`, severity: 'success' });
    }
  }, [dispatch, newCustomerName]);

  // ── Run History Handlers ─────────────────────────────────────────────────

  const handleRestoreRunPortfolio = useCallback((item: CPRunHistoryItem) => {
    if (location.pathname === '/cp/generate') {
      const f = item.filters_json || {};
      if (f.company) setGenCompany(f.company);
      if (item.customer_name && !f.company) setGenCompany(item.customer_name);
      if (f.depth != null) setGenDepth(f.depth);
      if (f.regions) setGenRegions(f.regions);
      if (f.countries) setGenCountries(f.countries);
      setGenInputs({});
      if (f.industry) setGenIndustry(f.industry);
      if (f.marketing_categories) setGenMarketingCategories(f.marketing_categories);
      if (f.product_category) setGenProductCategory(f.product_category);
      if (f.format) setGenFormat(f.format);
      else if (f.subcategories) setGenFormat(f.subcategories);
      if (f.positioning) setGenPositioning(Array.isArray(f.positioning) ? f.positioning : [f.positioning]);
      if (f.product_application) setGenProductApplication(Array.isArray(f.product_application) ? f.product_application : [f.product_application]);
      if (f.claims_filter) setGenClaimsFilter(f.claims_filter);
      if (f.features_filter) setGenFeaturesFilter(f.features_filter);
      if (f.serving_temperature) setGenServingTemperature(Array.isArray(f.serving_temperature) ? f.serving_temperature : [f.serving_temperature]);
      if (f.plant_based_categories) setGenPlantBasedCategories(f.plant_based_categories);
      setSelectedRunId(item.id);
      setSnackbar({ open: true, message: 'Form pre-filled from previous run', severity: 'info' });
      return;
    }

    let targetCustomerId: string | null = item.customer_id ?? null;

    if (!targetCustomerId) {
      const companyName = item.customer_name || item.filters_json?.company;
      if (!companyName) return;

      const normalize = (s: string) =>
        s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

      const customer = customers.find(c => normalize(c.name) === normalize(companyName));
      if (!customer) {
        setSnackbar({ open: true, message: `No customer found for "${companyName}"`, severity: 'error' });
        return;
      }
      targetCustomerId = customer.id;
    }

    dispatch(resetFilters());
    const f = item.filters_json || {};

    // Apply both the correct customer and generation_job_id in one dispatch
    // so the filter-clearing logic in setFilter doesn't strip generation_job_id
    dispatch(setFilter({
      customer_id: targetCustomerId,
      ...(f.generation_job_id ? { generation_job_id: f.generation_job_id } : {}),
    }));
    setSelectedRunId(item.id);
    const customerName = item.customer_name || customers.find(c => c.id === targetCustomerId)?.name || 'customer';
    setSnackbar({ open: true, message: `Showing portfolio for ${customerName}`, severity: 'success' });
  }, [customers, dispatch, location.pathname]);

  const handleDeleteRun = useCallback(async (id: string) => {
    const result = await dispatch(deleteRunHistory(id));
    if (deleteRunHistory.fulfilled.match(result)) {
      if (selectedRunId === id) setSelectedRunId(null);
      setSnackbar({ open: true, message: 'Run deleted', severity: 'success' });
      dispatch(fetchRunHistory());
    } else {
      setSnackbar({ open: true, message: 'Failed to delete run', severity: 'error' });
    }
  }, [dispatch, selectedRunId]);

  const handleDownloadRunCsv = useCallback(async (item: CPRunHistoryItem) => {
    try {
      const response = await apiClient.get(`/api/cp/run-history/${item.id}/csv`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const customerSlug = (item.customer_name || 'portfolio').toLowerCase().replace(/\s+/g, '_');
      a.download = `${customerSlug}_run_${item.created_at.slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setSnackbar({ open: true, message: 'Failed to download CSV', severity: 'error' });
    }
  }, []);

  const getBrandName = (product: CPProduct) => {
    if (!selectedCustomer?.brands || !product.brand_id) return '';
    return selectedCustomer.brands.find(b => b.id === product.brand_id)?.name || '';
  };

  // ── Table header styling ───────────────────────────────────────────────

  const thSx = {
    px: 1.5,
    py: 1.25,
    fontSize: '0.7rem',
    lineHeight: 1.3,
    fontWeight: 600,
    bgcolor: '#f1f5f9',
    color: 'text.primary',
    borderBottom: '2px solid',
    borderColor: 'divider',
    whiteSpace: 'nowrap' as const,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  };

  const stickyThSx = (left: number) => ({
    ...thSx,
    position: 'sticky' as const,
    left,
    zIndex: 5,
    bgcolor: '#f1f5f9',
    boxSizing: 'border-box' as const,
  });

  const tdSx = {
    px: 1.5,
    py: 1,
    fontSize: '0.8rem',
    lineHeight: 1.4,
    verticalAlign: 'top' as const,
  };

  const stickyTdSx = (left: number, bg: string) => ({
    ...tdSx,
    position: 'sticky' as const,
    left,
    zIndex: 2,
    bgcolor: bg,
    boxSizing: 'border-box' as const,
  });

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        display: 'flex',
        flexGrow: 1,
        height: '100%',
        overflow: 'hidden',
        width: '100%',
      }}
    >
      {/* Generate Tab */}
      {activeTab === 1 && isCpAdmin && (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
        <HistorySidebar
          items={runHistory}
          loading={runHistoryLoading}
          selectedRunId={selectedRunId}
          onRestore={handleRestoreRunPortfolio}
          onDelete={handleDeleteRun}
          onDownloadCsv={handleDownloadRunCsv}
          onRefresh={() => dispatch(fetchRunHistory())}
          isCpAdmin={isCpAdmin}
          isMobile={isMobile}
          isTablet={isTablet}
        />
        <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 1.5, md: 3 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 1200, mx: 'auto' }}>

            <Card
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 }, '&:last-child': { pb: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <AutoFixHigh sx={{ color: 'primary.main', fontSize: 22 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                    Generate Customer Profile
                  </Typography>
                </Box>
                <Grid container spacing={2}>

                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Company"
                      required
                      value={genCompany}
                      onChange={(e) => setGenCompany(e.target.value)}
                      placeholder="e.g. Danone"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Depth</InputLabel>
                      <Select
                        value={genDepth}
                        label="Depth"
                        onChange={(e) => setGenDepth(e.target.value as number)}
                        renderValue={(v) => DEPTH_LABELS[v as number] ?? ''}
                      >
                        {DEPTH_OPTIONS.map(opt => (
                          <MenuItem
                            key={opt.value}
                            value={opt.value}
                            sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}
                          >
                            {opt.label}
                            <Tooltip title={opt.description} placement="right" enterTouchDelay={0}>
                              <InfoOutlined
                                sx={{ fontSize: '1rem', color: 'text.secondary', flexShrink: 0, boxSizing: 'content-box', p: 0.5, mr: -0.5 }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </Tooltip>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      multiple
                      freeSolo
                      size="small"
                      options={referenceData?.regions || []}
                      value={genRegions}
                      inputValue={genInputs.regions || ''}
                      onInputChange={(_, v, reason) => { if (reason !== 'reset') setGenInputs(prev => ({ ...prev, regions: v })); }}
                      onChange={(_, v) => { setGenRegions(v as string[]); setGenInputs(prev => ({ ...prev, regions: '' })); }}
                      renderInput={(params) => <TextField {...params} label="Regions" placeholder="Select one or more" />}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip label={option} size="small" {...getTagProps({ index })} key={index} />
                        ))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      multiple
                      freeSolo
                      size="small"
                      options={referenceData?.countries || []}
                      value={genCountries}
                      inputValue={genInputs.countries || ''}
                      onInputChange={(_, v, reason) => { if (reason !== 'reset') setGenInputs(prev => ({ ...prev, countries: v })); }}
                      onChange={(_, v) => { setGenCountries(v as string[]); setGenInputs(prev => ({ ...prev, countries: '' })); }}
                      renderInput={(params) => <TextField {...params} label="Countries" placeholder="Type and press Enter" />}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip label={option} size="small" {...getTagProps({ index })} key={index} />
                        ))
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={referenceData?.industries || []}
                      value={genIndustry}
                      onInputChange={(_, v) => { setGenIndustry(v); setGenMarketingCategories([]); }}
                      renderInput={(params) => <TextField {...params} label="Industry" />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={5}>
                    <Autocomplete
                      multiple
                      freeSolo
                      size="small"
                      options={genIndustry ? (referenceData?.marketing_categories?.[genIndustry] || []) : Object.values(referenceData?.marketing_categories || {}).flat()}
                      value={genMarketingCategories}
                      inputValue={genInputs.marketing_categories || ''}
                      onInputChange={(_, v, reason) => { if (reason !== 'reset') setGenInputs(prev => ({ ...prev, marketing_categories: v })); }}
                      onChange={(_, v) => { setGenMarketingCategories(v as string[]); setGenInputs(prev => ({ ...prev, marketing_categories: '' })); }}
                      renderInput={(params) => <TextField {...params} label="Product Categories" placeholder="Select one or more" />}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip label={option} size="small" {...getTagProps({ index })} key={index} />
                        ))
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={referenceData?.product_categories || []}
                      value={genProductCategory}
                      onInputChange={(_, v) => setGenProductCategory(v)}
                      renderInput={(params) => <TextField {...params} label="AGRANA Product Type" />}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      multiple
                      freeSolo
                      size="small"
                      options={referenceData?.formats || []}
                      value={genFormat}
                      inputValue={genInputs.format || ''}
                      onInputChange={(_, v, reason) => { if (reason !== 'reset') setGenInputs(prev => ({ ...prev, format: v })); }}
                      onChange={(_, v) => { setGenFormat(v as string[]); setGenInputs(prev => ({ ...prev, format: '' })); }}
                      renderInput={(params) => <TextField {...params} label="Format" placeholder="Select one or more" />}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip label={option} size="small" {...getTagProps({ index })} key={index} />
                        ))
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      multiple
                      freeSolo
                      size="small"
                      options={referenceData?.positioning || []}
                      value={genPositioning}
                      inputValue={genInputs.positioning || ''}
                      onInputChange={(_, v, reason) => { if (reason !== 'reset') setGenInputs(prev => ({ ...prev, positioning: v })); }}
                      onChange={(_, v) => { setGenPositioning(v as string[]); setGenInputs(prev => ({ ...prev, positioning: '' })); }}
                      renderInput={(params) => <TextField {...params} label="Positioning" placeholder="Select one or more" />}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip label={option} size="small" {...getTagProps({ index })} key={index} />
                        ))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      multiple
                      freeSolo
                      size="small"
                      options={referenceData?.product_applications || []}
                      value={genProductApplication}
                      inputValue={genInputs.product_application || ''}
                      onInputChange={(_, v, reason) => { if (reason !== 'reset') setGenInputs(prev => ({ ...prev, product_application: v })); }}
                      onChange={(_, v) => { setGenProductApplication(v as string[]); setGenInputs(prev => ({ ...prev, product_application: '' })); }}
                      renderInput={(params) => <TextField {...params} label="Product Application" placeholder="Select one or more" />}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip label={option} size="small" {...getTagProps({ index })} key={index} />
                        ))
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      multiple
                      freeSolo
                      size="small"
                      options={referenceData?.serving_temperatures || []}
                      value={genServingTemperature}
                      inputValue={genInputs.serving_temperature || ''}
                      onInputChange={(_, v, reason) => { if (reason !== 'reset') setGenInputs(prev => ({ ...prev, serving_temperature: v })); }}
                      onChange={(_, v) => { setGenServingTemperature(v as string[]); setGenInputs(prev => ({ ...prev, serving_temperature: '' })); }}
                      renderInput={(params) => <TextField {...params} label="Serving Temperature" placeholder="Select one or more" />}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip label={option} size="small" {...getTagProps({ index })} key={index} />
                        ))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      multiple
                      freeSolo
                      size="small"
                      options={referenceData?.plant_based_categories || []}
                      value={genPlantBasedCategories}
                      inputValue={genInputs.plant_based_categories || ''}
                      onInputChange={(_, v, reason) => { if (reason !== 'reset') setGenInputs(prev => ({ ...prev, plant_based_categories: v })); }}
                      onChange={(_, v) => { setGenPlantBasedCategories(v as string[]); setGenInputs(prev => ({ ...prev, plant_based_categories: '' })); }}
                      renderInput={(params) => <TextField {...params} label="Plant-Based" placeholder="Select one or more" />}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip label={option} size="small" {...getTagProps({ index })} key={index} />
                        ))
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      multiple
                      freeSolo
                      size="small"
                      options={referenceData?.claims || []}
                      value={genClaimsFilter}
                      inputValue={genInputs.claims_filter || ''}
                      onInputChange={(_, v, reason) => { if (reason !== 'reset') setGenInputs(prev => ({ ...prev, claims_filter: v })); }}
                      onChange={(_, v) => { setGenClaimsFilter(v as string[]); setGenInputs(prev => ({ ...prev, claims_filter: '' })); }}
                      renderInput={(params) => <TextField {...params} label="Claims Filter" placeholder="Select one or more" />}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip label={option} size="small" {...getTagProps({ index })} key={index} />
                        ))
                      }
                    />
                  </Grid>

                  {SHOW_FEATURES_FILTER && (
                    <Grid item xs={12} md={6}>
                      <Autocomplete
                        multiple
                        freeSolo
                        size="small"
                        options={[]}
                        value={genFeaturesFilter}
                        inputValue={genInputs.features_filter || ''}
                        onInputChange={(_, v, reason) => { if (reason !== 'reset') setGenInputs(prev => ({ ...prev, features_filter: v })); }}
                        onChange={(_, v) => { setGenFeaturesFilter(v as string[]); setGenInputs(prev => ({ ...prev, features_filter: '' })); }}
                        renderInput={(params) => <TextField {...params} label="Features Filter (free text)" placeholder="e.g. On-to-go" />}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip label={option} size="small" {...getTagProps({ index })} key={index} />
                          ))
                        }
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', pt: 1.5 }}>
                    <Button
                      variant="contained"
                      onClick={handleSubmitGenerationJob}
                      disabled={!genCompany.trim() || submittingJob}
                      startIcon={submittingJob ? <CircularProgress size={16} color="inherit" /> : <AutoFixHigh />}
                      sx={{ px: 5, py: 1 }}
                      size="large"
                    >
                      {submittingJob ? 'Submitting...' : 'Generate Customer Portfolio'}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
              }}
            >
              <CardContent sx={{ p: { xs: 2, md: 3 }, '&:last-child': { pb: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                    Generation Jobs
                  </Typography>
                  <Button
                    size="small"
                    startIcon={generationJobsLoading ? <CircularProgress size={14} /> : <RefreshOutlined />}
                    onClick={() => dispatch(fetchGenerationJobs())}
                    disabled={generationJobsLoading}
                  >
                    Refresh
                  </Button>
                </Box>
                {generationJobs.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No generation jobs yet. Submit a job above to get started.
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer sx={{ borderRadius: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Company</TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Depth</TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Progress</TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Products Found</TableCell>
                          <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Created</TableCell>
                          <TableCell sx={{ bgcolor: '#f8fafc' }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {generationJobs.map((job) => {
                          const statusLabel: Record<string, string> = {
                            accepted: 'Queued',
                            pending: 'Queued',
                            running: 'In Progress',
                            completed: 'Done',
                            failed: 'Failed',
                            cancelled: 'Cancelled',
                          };
                          const isActive = job.status === 'accepted' || job.status === 'pending' || job.status === 'running';
                          const depthVal = job.params_json?.depth;
                          const depthLabel = depthVal != null ? (DEPTH_LABELS[depthVal] ?? `Depth ${depthVal}`) : (job.params_json?.num_products ?? '-');
                          return (
                          <TableRow key={job.id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                            <TableCell sx={{ fontWeight: 500 }}>{job.company}</TableCell>
                            <TableCell>{depthLabel}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={statusLabel[job.status] || job.status}
                                color={
                                  job.status === 'completed' ? 'success' :
                                  job.status === 'failed' ? 'error' :
                                  job.status === 'running' ? 'primary' : 'default'
                                }
                                sx={{ fontSize: '0.7rem' }}
                              />
                            </TableCell>
                            <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <Tooltip title={job.progress || ''}>
                                <span>{job.progress || '-'}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell>{job.products_found}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {formatDate(job.created_at)}
                            </TableCell>
                            <TableCell>
                              {isActive && (
                                <Tooltip title="Poll status">
                                  <IconButton size="small" onClick={() => handlePollJob(job.id)}>
                                    <RefreshOutlined fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {isActive && (
                                <Tooltip title="Cancel job">
                                  <IconButton size="small" color="error" onClick={() => handleCancelJob(job.id)}>
                                    <StopCircleOutlined fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {job.status === 'cancelled' && (
                                <Tooltip title={job.progress || 'Cancelled'}>
                                  <CancelOutlined sx={{ color: 'text.disabled', fontSize: 20 }} />
                                </Tooltip>
                              )}
                              {job.status === 'completed' && (
                                <CheckCircleOutline sx={{ color: 'success.main', fontSize: 20 }} />
                              )}
                              {job.status === 'failed' && (
                                <Tooltip title={job.error_message || 'Failed'}>
                                  <CancelOutlined sx={{ color: 'error.main', fontSize: 20 }} />
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Box>
        </Box>
        </Box>
      )}

      {activeTab === 1 && !isCpAdmin && (
        <Box display="flex" alignItems="center" justifyContent="center" height="100%" flex={1}>
          <Typography color="text.secondary">You don't have permission to access the Generate tab.</Typography>
        </Box>
      )}

      {/* Portfolio Tab */}
      {activeTab === 0 && (
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>

        <HistorySidebar
          items={runHistory}
          loading={runHistoryLoading}
          selectedRunId={selectedRunId}
          onRestore={handleRestoreRunPortfolio}
          onDelete={handleDeleteRun}
          onDownloadCsv={handleDownloadRunCsv}
          onRefresh={() => dispatch(fetchRunHistory())}
          isCpAdmin={isCpAdmin}
          isMobile={isMobile}
          isTablet={isTablet}
        />

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, p: isMobile ? 1 : 1.5 }}>

          {/* Collapsible Filters */}
          <Card
            elevation={0}
            sx={{
              mb: 1.5,
              flexShrink: 0,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 0.75,
                cursor: 'pointer',
                transition: 'background-color 0.15s',
                '&:hover': { bgcolor: 'action.hover' },
              }}
              onClick={() => setFiltersExpanded(v => !v)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <FilterList sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}>
                  Filters
                </Typography>
                {!filtersExpanded && (
                  <FilterSummary filters={filters} customers={customers} />
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {isCpAdmin && (
                  <Tooltip title="Add new customer">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setNewCustomerDialogOpen(true); }} sx={{ p: 0.4, color: 'primary.main' }}>
                      <AddCircleOutline sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Reset all filters">
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); dispatch(resetFilters()); setSelectedRunId(null); }} sx={{ p: 0.4, color: 'text.secondary' }}>
                    <FilterListOff sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                {filtersExpanded ? <ExpandLess sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />}
              </Box>
            </Box>
            <Collapse in={filtersExpanded}>
              <Divider />
              <Box sx={{ px: 2, py: 1.5 }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Customer</InputLabel>
                      <Select
                        value={filters.customer_id || ''}
                        label="Customer"
                        onChange={(e) => {
                          const newId = (e.target.value as string) || null;
                          dispatch(resetFilters());
                          dispatch(setFilter({ customer_id: newId }));
                        }}
                        disabled={customersLoading}
                      >
                        <MenuItem value=""><em>All</em></MenuItem>
                        {customers.map((c) => (
                          <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={4} md={1.5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Region</InputLabel>
                      <Select value={filters.region} label="Region" onChange={(e) => dispatch(setFilter({ region: e.target.value as string }))}>
                        <MenuItem value="">All</MenuItem>
                        {(referenceData?.regions || []).map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={4} md={1.5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Country</InputLabel>
                      <Select value={filters.country} label="Country" onChange={(e) => dispatch(setFilter({ country: e.target.value as string }))}>
                        <MenuItem value="">All</MenuItem>
                        {(referenceData?.countries || []).map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={4} md={1.5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Industry</InputLabel>
                      <Select
                        value={filters.industry}
                        label="Industry"
                        onChange={(e) => {
                          dispatch(setFilter({ industry: e.target.value as string, marketing_category: '' }));
                        }}
                      >
                        <MenuItem value="">All</MenuItem>
                        {(referenceData?.industries || []).map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Product Category</InputLabel>
                      <Select
                        value={filters.marketing_category}
                        label="Product Category"
                        onChange={(e) => dispatch(setFilter({ marketing_category: e.target.value as string }))}
                      >
                        {marketingMenuItems}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={4} md={1.5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>AGRANA Product Type</InputLabel>
                      <Select value={filters.product_category} label="AGRANA Product Type" onChange={(e) => dispatch(setFilter({ product_category: e.target.value as string }))}>
                        <MenuItem value="">All</MenuItem>
                        {(referenceData?.product_categories || []).map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={4} md={1.5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Subcategory</InputLabel>
                      <Select value={filters.subcategory} label="Subcategory" onChange={(e) => dispatch(setFilter({ subcategory: e.target.value as string }))}>
                        <MenuItem value="">All</MenuItem>
                        {(referenceData?.formats || []).map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={4} md={1.5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Positioning</InputLabel>
                      <Select value={filters.positioning} label="Positioning" onChange={(e) => dispatch(setFilter({ positioning: e.target.value as string }))}>
                        <MenuItem value="">All</MenuItem>
                        {(referenceData?.positioning || []).map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={4} md={1.5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Product Application</InputLabel>
                      <Select value={filters.product_application} label="Product Application" onChange={(e) => dispatch(setFilter({ product_application: e.target.value as string }))}>
                        <MenuItem value="">All</MenuItem>
                        {(referenceData?.product_applications || []).map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={4} md={1.5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Serving Temperature</InputLabel>
                      <Select value={filters.serving_temperature} label="Serving Temperature" onChange={(e) => dispatch(setFilter({ serving_temperature: e.target.value as string }))}>
                        <MenuItem value="">All</MenuItem>
                        {(referenceData?.serving_temperatures || []).map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={4} md={1.5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Plant-based Category</InputLabel>
                      <Select value={filters.plant_based_category} label="Plant-based Category" onChange={(e) => dispatch(setFilter({ plant_based_category: e.target.value as string }))}>
                        <MenuItem value="">All</MenuItem>
                        {(referenceData?.plant_based_categories || []).map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={4} md={1.5}>
                    <FormControl fullWidth size="small">
                      <InputLabel>AGRANA Present</InputLabel>
                      <Select value={filters.agrana_present} label="AGRANA Present" onChange={(e) => dispatch(setFilter({ agrana_present: e.target.value as string }))}>
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="true">Yes</MenuItem>
                        <MenuItem value="false">No</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                      multiple
                      size="small"
                      options={referenceData?.claims || []}
                      value={filters.claims}
                      onChange={(_, newValue) => dispatch(setFilter({ claims: newValue }))}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            {...getTagProps({ index })}
                            key={option}
                            label={option}
                            size="small"
                            variant="outlined"
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Claims" placeholder="Search claims..." />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search"
                      placeholder="Product name, description, ingredients..."
                      value={filters.search}
                      onChange={(e) => dispatch(setFilter({ search: e.target.value }))}
                      InputProps={{
                        endAdornment: filters.search ? (
                          <IconButton size="small" onClick={() => dispatch(setFilter({ search: '' }))}>
                            <Close sx={{ fontSize: 14 }} />
                          </IconButton>
                        ) : null,
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </Card>

          {/* Data Table Card */}
          <Card
            elevation={0}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                px: 2,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                flexShrink: 0,
                bgcolor: 'background.paper',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
                  {selectedCustomer?.name || 'Product Portfolio'}
                </Typography>
                <Chip
                  label={`${totalProducts} product${totalProducts !== 1 ? 's' : ''}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontSize: '0.65rem', height: 22, fontWeight: 600 }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                <Tooltip title={showAllColumns ? 'Show fewer columns' : 'Show all columns'}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setShowAllColumns(v => !v)}
                    startIcon={<ViewColumn sx={{ fontSize: '1rem !important' }} />}
                    sx={{ fontSize: '0.7rem', py: 0.25, minWidth: 'auto', textTransform: 'none', display: 'inline-flex', alignItems: 'center', lineHeight: 1.5, '& .MuiButton-startIcon': { mr: 0.5, display: 'flex', alignItems: 'center' } }}
                  >
                    {showAllColumns ? 'Fewer columns' : 'All columns'}
                  </Button>
                </Tooltip>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                {SHOW_IMPORT_BUTTON && isCpAdmin && (
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={<FileUploadOutlined sx={{ fontSize: '1rem !important' }} />}
                    onClick={() => setImportDialogOpen(true)}
                    sx={{ fontSize: '0.7rem', py: 0.25, display: 'inline-flex', alignItems: 'center', lineHeight: 1.5, '& .MuiButton-startIcon': { mr: 0.5, display: 'flex', alignItems: 'center' } }}
                  >
                    Import
                  </Button>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FileDownloadOutlined sx={{ fontSize: '1rem !important' }} />}
                  onClick={handleExportCsv}
                  disabled={products.length === 0}
                  sx={{ fontSize: '0.7rem', py: 0.25, display: 'inline-flex', alignItems: 'center', lineHeight: 1.5, '& .MuiButton-startIcon': { mr: 0.5, display: 'flex', alignItems: 'center' } }}
                >
                  CSV
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<FileDownloadOutlined sx={{ fontSize: '1rem !important' }} />}
                  onClick={handleExportExcel}
                  disabled={products.length === 0}
                  sx={{ fontSize: '0.7rem', py: 0.25, display: 'inline-flex', alignItems: 'center', lineHeight: 1.5, '& .MuiButton-startIcon': { mr: 0.5, display: 'flex', alignItems: 'center' } }}
                >
                  Excel
                </Button>
              </Box>
            </Box>

            {loading && <LinearProgress sx={{ flexShrink: 0 }} />}

            <TableContainer sx={{
              flex: 1,
              overflow: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(0,0,0,0.15) transparent',
              '&::-webkit-scrollbar': { height: 8 },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.15)', borderRadius: 4 },
              '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(0,0,0,0.25)' },
            }}>
              <Table
                size="small"
                stickyHeader
                sx={{
                  '& td, & th': {
                    borderBottom: '1px solid',
                    borderColor: 'rgba(0,0,0,0.06)',
                    borderRight: '1px solid rgba(0,0,0,0.06)',
                  },
                  '& td:last-child, & th:last-child': {
                    borderRight: 'none',
                  },
                  '& tbody tr:nth-of-type(even)': {
                    bgcolor: '#f8f8f8',
                  },
                  '& tbody tr:nth-of-type(odd)': {
                    bgcolor: '#ffffff',
                  },
                  '& tbody tr': {
                    transition: 'background-color 120ms ease-in-out',
                  },
                  '& tbody tr:hover td': {
                    bgcolor: '#fdf2f4 !important',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...stickyThSx(0), width: 140, minWidth: 140, maxWidth: 140 }}>Company</TableCell>
                    <TableCell sx={{ ...stickyThSx(140), width: 200, minWidth: 200, maxWidth: 200 }}>Product Name</TableCell>
                    <TableCell sx={thSx}>Format</TableCell>
                    <TableCell sx={thSx}>Region</TableCell>
                    <TableCell sx={thSx}>Country</TableCell>
                    <TableCell sx={thSx}>AGRANA Product Type</TableCell>
                    <TableCell sx={thSx}>Positioning</TableCell>
                    <TableCell sx={{ ...thSx, minWidth: 200 }}>Claims</TableCell>
                    <TableCell sx={thSx}>AGRANA Present</TableCell>
                    <TableCell sx={thSx}>Source URL</TableCell>
                    {showAllColumns && (
                      <>
                        <TableCell sx={thSx}>Brand</TableCell>
                        <TableCell sx={thSx}>Industry</TableCell>
                        <TableCell sx={thSx}>Product Category</TableCell>
                        <TableCell sx={thSx}>Product Application</TableCell>
                        <TableCell sx={thSx}>Serving Temperature</TableCell>
                        <TableCell sx={thSx}>Plant-based Category</TableCell>
                        <TableCell sx={{ ...thSx, minWidth: 200 }}>Description</TableCell>
                        <TableCell sx={thSx}>Flavors</TableCell>
                        <TableCell sx={thSx}>Ingredients</TableCell>
                        <TableCell sx={thSx}>Category</TableCell>
                        <TableCell sx={thSx}>Subcategory</TableCell>
                        <TableCell sx={thSx}>Is Fruit Preparation Likely</TableCell>
                        <TableCell sx={{ ...thSx, minWidth: 160 }}>Features</TableCell>
                        <TableCell sx={thSx}>AGRANA Recipe Code</TableCell>
                        <TableCell sx={thSx}>AGRANA Volume (Tons)</TableCell>
                        <TableCell sx={thSx}>AGRANA Plant</TableCell>
                        <TableCell sx={thSx}>Data Source</TableCell>
                        <TableCell sx={{ ...thSx, minWidth: 160 }}>Notes</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.map((r, idx) => {
                    const rowBg = idx % 2 === 0 ? '#ffffff' : '#f8f8f8';
                    const claimsText = r.claims?.filter(c => ['health','dietary','certification'].includes(c.claim_type)).map(c => c.claim_value).join(', ') || '';
                    const featuresText = r.claims?.filter(c => ['functional','other'].includes(c.claim_type)).map(c => c.claim_value).join(', ') || '';
                    return (
                    <TableRow key={r.id || idx}>
                      <TableCell sx={{ ...stickyTdSx(0, rowBg), width: 140, minWidth: 140, maxWidth: 140 }}>
                          <TruncCell text={customers.find(c => c.id === r.customer_id)?.name || ''} maxWidth={115} sx={{ fontWeight: 600 }} />
                      </TableCell>
                      <TableCell sx={{ ...stickyTdSx(140, rowBg), width: 200, minWidth: 200, maxWidth: 200 }}>
                          <TruncCell text={r.product_name} maxWidth={175} sx={{ fontWeight: 500 }} />
                      </TableCell>
                      <TableCell sx={tdSx}><TruncCell text={r.format} maxWidth={100} /></TableCell>
                      <TableCell sx={tdSx}>{r.region}</TableCell>
                      <TableCell sx={tdSx}>{r.country}</TableCell>
                      <TableCell sx={tdSx}><TruncCell text={r.product_category} maxWidth={130} /></TableCell>
                      <TableCell sx={tdSx}><TruncCell text={r.positioning} maxWidth={100} /></TableCell>
                      <TableCell sx={{ ...tdSx, minWidth: 200 }}>
                        <ClaimsChips claims={claimsText} />
                      </TableCell>
                      <TableCell sx={{ ...tdSx, textAlign: 'center' }}>
                        <Tooltip title={r.agrana_present ? 'AGRANA Present' : 'Not Present'}>
                          {r.agrana_present ? (
                            <CheckCircleOutline sx={{ fontSize: 16, color: 'success.main' }} />
                          ) : (
                            <CancelOutlined sx={{ fontSize: 16, color: 'text.disabled' }} />
                          )}
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={tdSx}>
                        {r.product_url ? (
                          <Tooltip title={r.product_url}>
                            <IconButton
                              size="small"
                              component="a"
                              href={r.product_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ p: 0.25, color: 'primary.main' }}
                            >
                              <OpenInNew sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </TableCell>
                      {showAllColumns && (
                        <>
                          <TableCell sx={tdSx}><TruncCell text={getBrandName(r)} maxWidth={100} /></TableCell>
                          <TableCell sx={tdSx}><TruncCell text={r.industry} maxWidth={100} /></TableCell>
                          <TableCell sx={tdSx}><TruncCell text={r.marketing_category} maxWidth={110} /></TableCell>
                          <TableCell sx={tdSx}><TruncCell text={r.product_application} maxWidth={110} /></TableCell>
                          <TableCell sx={tdSx}><TruncCell text={r.serving_temperature} maxWidth={100} /></TableCell>
                          <TableCell sx={tdSx}><TruncCell text={r.plant_based_category} maxWidth={100} /></TableCell>
                          <TableCell sx={{ ...tdSx, minWidth: 200 }}><TruncCell text={r.description} maxWidth={200} /></TableCell>
                          <TableCell sx={tdSx}><TruncCell text={r.flavors} maxWidth={120} /></TableCell>
                          <TableCell sx={tdSx}><TruncCell text={r.ingredients} maxWidth={150} /></TableCell>
                          <TableCell sx={tdSx}><TruncCell text={r.category} maxWidth={100} /></TableCell>
                          <TableCell sx={tdSx}><TruncCell text={r.subcategory} maxWidth={100} /></TableCell>
                          <TableCell sx={{ ...tdSx, textAlign: 'center' }}>
                            {r.is_fruit_preparation_likely ? (
                              <CheckCircleOutline sx={{ fontSize: 16, color: 'success.main' }} />
                            ) : (
                              <CancelOutlined sx={{ fontSize: 16, color: 'text.disabled' }} />
                            )}
                          </TableCell>
                          <TableCell sx={{ ...tdSx, minWidth: 160 }}>
                            <ClaimsChips claims={featuresText} />
                          </TableCell>
                          <TableCell sx={tdSx}><TruncCell text={r.agrana_recipe_code} maxWidth={120} /></TableCell>
                          <TableCell sx={tdSx}>{r.agrana_volume_tons ?? ''}</TableCell>
                          <TableCell sx={tdSx}><TruncCell text={r.agrana_plant} maxWidth={100} /></TableCell>
                          <TableCell sx={tdSx}>{r.data_source ?? ''}</TableCell>
                          <TableCell sx={{ ...tdSx, minWidth: 160 }}><TruncCell text={r.notes} maxWidth={160} /></TableCell>
                        </>
                      )}
                    </TableRow>
                    );
                  })}
                  {!loading && products.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={showAllColumns ? 28 : 10} sx={{ py: 8, border: 'none' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                          <SearchOff sx={{ fontSize: 48, color: 'text.disabled' }} />
                          <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                            {customers.length === 0
                              ? 'No customers yet'
                              : 'No products found'}
                          </Typography>
                          <Typography variant="body2" color="text.disabled">
                            {customers.length === 0
                              ? 'Create a customer and import data to get started.'
                              : 'Try adjusting your filters, or select a run from the sidebar.'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

        </Box>
      </Box>
      )}

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => { setImportDialogOpen(false); dispatch(clearImportResult()); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Import Product Data
          <IconButton size="small" onClick={() => { setImportDialogOpen(false); dispatch(clearImportResult()); }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload a CSV or Excel file with product data. Columns will be auto-mapped.
          </Typography>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Data Source</InputLabel>
            <Select value={importSourceType} label="Data Source" onChange={(e) => setImportSourceType(e.target.value)}>
              <MenuItem value="manual">Manual / Brand Website Data</MenuItem>
              <MenuItem value="mintel_import">Mintel Export</MenuItem>
              <MenuItem value="agrana_internal">AGRANA Internal Data</MenuItem>
            </Select>
          </FormControl>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {importSourceType === 'agrana_internal'
              ? 'AGRANA internal imports will automatically set "AGRANA Present" to Yes for all rows.'
              : importSourceType === 'mintel_import'
              ? 'Mintel imports will map launch date and price positioning fields.'
              : 'Upload product data from any source. Columns will be auto-detected.'}
          </Typography>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            style={{ display: 'none' }}
            onChange={handleFileImport}
          />

          {importing ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CircularProgress size={32} />
              <Typography variant="body2" sx={{ mt: 1 }}>Importing...</Typography>
            </Box>
          ) : (
            <Box
              sx={{
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': { borderColor: 'primary.main', backgroundColor: 'action.hover' },
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <FileUploadOutlined sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body2">Click to upload CSV or Excel file</Typography>
              <Typography variant="caption" color="text.secondary">
                Supported: .csv, .xlsx, .xls
              </Typography>
            </Box>
          )}

          {importResult && (
            <Alert severity={importResult.rows_failed > 0 ? 'warning' : 'success'} sx={{ mt: 2 }}>
              <Typography variant="body2">
                Imported: {importResult.rows_imported} | Updated: {importResult.rows_updated} | Failed: {importResult.rows_failed}
              </Typography>
              {importResult.errors.length > 0 && (
                <Box sx={{ mt: 1, maxHeight: 100, overflow: 'auto' }}>
                  {importResult.errors.slice(0, 5).map((err, i) => (
                    <Typography key={i} variant="caption" display="block" color="text.secondary">
                      Row {err.row}: {err.error}
                    </Typography>
                  ))}
                  {importResult.errors.length > 5 && (
                    <Typography variant="caption" color="text.secondary">
                      ...and {importResult.errors.length - 5} more errors
                    </Typography>
                  )}
                </Box>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={handleDownloadTemplate}>
            Download Template
          </Button>
          <Button onClick={() => { setImportDialogOpen(false); dispatch(clearImportResult()); }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Customer Dialog */}
      <Dialog open={newCustomerDialogOpen} onClose={() => { setNewCustomerDialogOpen(false); setNewCustomerName(''); }} maxWidth="xs" fullWidth>
        <DialogTitle>Add New Customer</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Customer Name"
            value={newCustomerName}
            onChange={(e) => setNewCustomerName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateCustomer()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setNewCustomerDialogOpen(false); setNewCustomerName(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateCustomer} disabled={!newCustomerName.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => dispatch(clearError())}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => dispatch(clearError())} severity="error" variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerProfiling;
